/**
 * Where the sun, the moon and the stars are, for any instant and any place.
 *
 * - **Sun**: the NOAA / Meeus low-precision solar equations — mean longitude
 *   and anomaly, the equation of centre, obliquity, then right ascension and
 *   declination and the local hour angle. Good to about 0.01°, which is far
 *   below a pixel.
 * - **Moon**: Paul Schlyter's method — the lunar orbital elements, Kepler's
 *   equation, and the twelve largest perturbation terms in longitude (evection,
 *   variation, the yearly equation...) and five in latitude, then the
 *   topocentric parallax correction, which alone moves the moon by up to a
 *   degree. Good to a couple of arcminutes.
 * - **Phase** falls out of the geometry: elongation from the sun gives the
 *   phase angle and illuminated fraction, and the renderer lights the moon from
 *   the true sun direction so the crescent points the right way on its own.
 * - **Stars** turn with local sidereal time about the real celestial pole.
 *
 * All angles out are radians. Azimuth is measured from north toward east, so
 * in the scene (X east, Y up, Z south) a body at azimuth A and altitude h is in
 * direction (sin A cos h, sin h, -cos A cos h) — see `horizonToScene`.
 */

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

function norm360(x: number): number {
  return ((x % 360) + 360) % 360;
}

/** Julian day number of an instant. */
export function julianDay(date: Date): number {
  return date.getTime() / 86_400_000 + 2440587.5;
}

/** Greenwich mean sidereal time, degrees. */
export function greenwichSidereal(date: Date): number {
  const d = julianDay(date) - 2451545.0;
  return norm360(280.46061837 + 360.98564736629 * d);
}

export interface Horizontal {
  /** Above the horizon, radians; refraction included where it applies. */
  altitude: number;
  /** From north through east, radians, 0..2π. */
  azimuth: number;
}

/** Equatorial (right ascension, declination, degrees) to local horizontal. */
function toHorizontal(ra: number, dec: number, lat: number, lon: number, date: Date): Horizontal {
  const hourAngle = (greenwichSidereal(date) + lon - ra) * RAD;
  const phi = lat * RAD;
  const delta = dec * RAD;
  const altitude = Math.asin(Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(hourAngle));
  const azimuth = Math.atan2(
    -Math.cos(delta) * Math.sin(hourAngle),
    Math.sin(delta) * Math.cos(phi) - Math.cos(delta) * Math.cos(hourAngle) * Math.sin(phi),
  );
  return { altitude, azimuth: (azimuth + 2 * Math.PI) % (2 * Math.PI) };
}

/**
 * Atmospheric refraction, radians, for an apparent altitude — Saemundsson's
 * formula. Lifts the sun by half a degree at the horizon, which is why it is
 * still visible after it has geometrically set.
 */
function refraction(altitude: number): number {
  const h = altitude * DEG;
  if (h < -1) return 0;
  return (1.02 / Math.tan((h + 10.3 / (h + 5.11)) * RAD)) / 60 * RAD;
}

/** Ecliptic → equatorial, degrees in and out. */
function eclipticToEquatorial(lon: number, lat: number, obliquity: number): { ra: number; dec: number } {
  const l = lon * RAD, b = lat * RAD, e = obliquity * RAD;
  const ra = Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l));
  const dec = Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l));
  return { ra: norm360(ra * DEG), dec: dec * DEG };
}

/** The sun's apparent ecliptic longitude, degrees. */
function solarLongitude(date: Date): number {
  const n = julianDay(date) - 2451545.0;
  const L = norm360(280.46 + 0.9856474 * n);
  const g = norm360(357.528 + 0.9856003 * n) * RAD;
  return norm360(L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g));
}

function obliquity(date: Date): number {
  return 23.439 - 0.0000004 * (julianDay(date) - 2451545.0);
}

export function sunPosition(date: Date, lat: number, lon: number): Horizontal {
  const { ra, dec } = eclipticToEquatorial(solarLongitude(date), 0, obliquity(date));
  const h = toHorizontal(ra, dec, lat, lon, date);
  return { altitude: h.altitude + refraction(h.altitude), azimuth: h.azimuth };
}

export interface MoonState extends Horizontal {
  /** Earth–moon distance, km. */
  distance: number;
  /** Fraction of the disc lit, 0 new .. 1 full. */
  illumination: number;
  /** 0 new, 0.25 first quarter, 0.5 full, 0.75 last quarter. */
  phase: number;
  /** Angular diameter, radians. */
  diameter: number;
}

/** The moon's geocentric ecliptic position — Schlyter, with perturbations. */
function lunarEcliptic(date: Date): { lon: number; lat: number; r: number } {
  // Schlyter counts days from 2000 Jan 0.0 UT.
  const d = julianDay(date) - 2451543.5;
  const N = norm360(125.1228 - 0.0529538083 * d);
  const i = 5.1454;
  const w = norm360(318.0634 + 0.1643573223 * d);
  const a = 60.2666;
  const e = 0.0549;
  const M = norm360(115.3654 + 13.0649929509 * d);

  // Kepler's equation, a few Newton steps.
  let E = M * RAD + e * Math.sin(M * RAD) * (1 + e * Math.cos(M * RAD));
  for (let k = 0; k < 5; k++) E -= (E - e * Math.sin(E) - M * RAD) / (1 - e * Math.cos(E));

  const xv = a * (Math.cos(E) - e);
  const yv = a * Math.sqrt(1 - e * e) * Math.sin(E);
  const v = Math.atan2(yv, xv) * DEG;
  let r = Math.hypot(xv, yv);

  const Nr = N * RAD, ir = i * RAD, vw = (v + w) * RAD;
  const xh = r * (Math.cos(Nr) * Math.cos(vw) - Math.sin(Nr) * Math.sin(vw) * Math.cos(ir));
  const yh = r * (Math.sin(Nr) * Math.cos(vw) + Math.cos(Nr) * Math.sin(vw) * Math.cos(ir));
  const zh = r * Math.sin(vw) * Math.sin(ir);
  let lon = norm360(Math.atan2(yh, xh) * DEG);
  let lat = Math.atan2(zh, Math.hypot(xh, yh)) * DEG;

  // Perturbations, from the sun's and moon's mean arguments.
  const Ms = norm360(356.047 + 0.9856002585 * d) * RAD;
  const ws = 282.9404 + 4.70935e-5 * d;
  const Mm = M * RAD;
  const Ls = norm360(Ms * DEG + ws) * RAD;
  const Lm = norm360(N + w + M) * RAD;
  const D = Lm - Ls;
  const F = Lm - Nr;
  lon += -1.274 * Math.sin(Mm - 2 * D) + 0.658 * Math.sin(2 * D) - 0.186 * Math.sin(Ms)
    - 0.059 * Math.sin(2 * Mm - 2 * D) - 0.057 * Math.sin(Mm - 2 * D + Ms) + 0.053 * Math.sin(Mm + 2 * D)
    + 0.046 * Math.sin(2 * D - Ms) + 0.041 * Math.sin(Mm - Ms) - 0.035 * Math.sin(D)
    - 0.031 * Math.sin(Mm + Ms) - 0.015 * Math.sin(2 * F - 2 * D) + 0.011 * Math.sin(Mm - 4 * D);
  lat += -0.173 * Math.sin(F - 2 * D) - 0.055 * Math.sin(Mm - F - 2 * D) - 0.046 * Math.sin(Mm + F - 2 * D)
    + 0.033 * Math.sin(F + 2 * D) + 0.017 * Math.sin(2 * Mm + F);
  r += -0.58 * Math.cos(Mm - 2 * D) - 0.46 * Math.cos(2 * D);
  return { lon: norm360(lon), lat, r };
}

const EARTH_RADIUS_KM = 6378.14;
const MOON_RADIUS_KM = 1737.4;

export function moonPosition(date: Date, lat: number, lon: number): MoonState {
  const m = lunarEcliptic(date);
  const { ra, dec } = eclipticToEquatorial(m.lon, m.lat, obliquity(date));
  const geo = toHorizontal(ra, dec, lat, lon, date);
  // Topocentric parallax: seen from the surface rather than the earth's
  // centre, the moon sits lower by up to its horizontal parallax, ~1°.
  const parallax = Math.asin(1 / m.r);
  const altitude = geo.altitude - parallax * Math.cos(geo.altitude);

  // Phase from the elongation between moon and sun.
  const sunLon = solarLongitude(date);
  const elongation = Math.acos(Math.cos((m.lon - sunLon) * RAD) * Math.cos(m.lat * RAD));
  const phaseAngle = Math.PI - elongation;
  const illumination = (1 + Math.cos(phaseAngle)) / 2;
  const waxing = norm360(m.lon - sunLon) < 180;
  const phase = waxing ? elongation / (2 * Math.PI) : 1 - elongation / (2 * Math.PI);

  const distance = m.r * EARTH_RADIUS_KM;
  return {
    altitude: altitude + refraction(altitude),
    azimuth: geo.azimuth,
    distance,
    illumination,
    phase,
    diameter: 2 * Math.atan(MOON_RADIUS_KM / distance),
  };
}

/**
 * Rotation from the equatorial frame into the local horizon frame, as the
 * local sidereal time and the latitude — what the star field needs.
 */
export function localSidereal(date: Date, lon: number): number {
  return norm360(greenwichSidereal(date) + lon) * RAD;
}

/** Horizontal coordinates to a scene direction: X east, Y up, Z south. */
export function horizonToScene(h: Horizontal): [number, number, number] {
  const c = Math.cos(h.altitude);
  return [Math.sin(h.azimuth) * c, Math.sin(h.altitude), -Math.cos(h.azimuth) * c];
}

/** A star's equatorial position (degrees) to local horizontal. */
export function starPosition(ra: number, dec: number, date: Date, lat: number, lon: number): Horizontal {
  return toHorizontal(ra, dec, lat, lon, date);
}

/** A point on the ground, degrees. */
export interface GroundPoint {
  lat: number;
  lon: number;
}

function subpoint(ra: number, dec: number, date: Date): GroundPoint {
  // Directly overhead where the local sidereal time equals the right
  // ascension: longitude = RA - GMST.
  const lon = ((ra - greenwichSidereal(date) + 540) % 360) - 180;
  return { lat: dec, lon };
}

/** Where the sun is straight overhead right now. */
export function subsolarPoint(date: Date): GroundPoint {
  const { ra, dec } = eclipticToEquatorial(solarLongitude(date), 0, obliquity(date));
  return subpoint(ra, dec, date);
}

/** Where the moon is straight overhead right now (geocentric). */
export function sublunarPoint(date: Date): GroundPoint {
  const m = lunarEcliptic(date);
  const { ra, dec } = eclipticToEquatorial(m.lon, m.lat, obliquity(date));
  return subpoint(ra, dec, date);
}

/**
 * The sun's altitude at a place, from the subsolar point: the fast form of
 * `sunPosition` for colouring a whole map, one call per pixel. Refraction
 * and the equation of time are already inside the subsolar point.
 */
export function sunAltitudeFrom(sub: GroundPoint, lat: number, lon: number): number {
  const phi = lat * RAD, d = sub.lat * RAD;
  return Math.asin(Math.sin(phi) * Math.sin(d) + Math.cos(phi) * Math.cos(d) * Math.cos((lon - sub.lon) * RAD));
}

/** Name the moon's phase, for the HUD. */
export function phaseName(phase: number): string {
  const names = ['new moon', 'waxing crescent', 'first quarter', 'waxing gibbous',
    'full moon', 'waning gibbous', 'last quarter', 'waning crescent'];
  return names[Math.round(phase * 8) % 8];
}
